import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiKeysApi } from '../services/api';
import { useTenantContextStore } from '../store/tenant-context.store';
import {
  Key,
  Plus,
  Trash2,
  RefreshCw,
  Copy,
  Check,
  AlertTriangle,
  ToggleLeft,
  ToggleRight,
  Loader2,
  Shield,
  Clock,
  X,
} from 'lucide-react';
import clsx from 'clsx';

// ============================================================================
// Types
// ============================================================================

interface ApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  permissions: string[];
  expiresAt: string | null;
  lastUsedAt: string | null;
  isActive: boolean;
  createdAt: string;
  rawKey?: string;
}

const AVAILABLE_PERMISSIONS = [
  { value: 'devices:read', label: 'Appareils (lecture)' },
  { value: 'devices:write', label: 'Appareils (écriture)' },
  { value: 'positions:read', label: 'Positions (lecture)' },
  { value: 'geofences:read', label: 'Géofences (lecture)' },
  { value: 'geofences:write', label: 'Géofences (écriture)' },
  { value: 'alerts:read', label: 'Alertes (lecture)' },
  { value: 'alerts:write', label: 'Alertes (écriture)' },
  { value: 'drivers:read', label: 'Conducteurs (lecture)' },
  { value: 'reports:read', label: 'Rapports (lecture)' },
];

// ============================================================================
// Page
// ============================================================================

export default function ApiKeysPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { selectedTenant } = useTenantContextStore();
  const tenantKey = selectedTenant?.id || 'default';
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['api-keys', tenantKey],
    queryFn: () => apiKeysApi.list(),
    retry: (failureCount, err: any) => {
      // Don't retry if API keys are disabled
      if (err?.response?.data?.code === 'API_KEYS_DISABLED') return false;
      return failureCount < 3;
    },
  });

  const isDisabled = (error as any)?.response?.data?.code === 'API_KEYS_DISABLED';
  const keys: ApiKey[] = data?.data?.data || [];

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      apiKeysApi.update(id, { isActive }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['api-keys'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiKeysApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      setConfirmDelete(null);
    },
  });

  const rotateMutation = useMutation({
    mutationFn: (id: string) => apiKeysApi.rotate(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      setNewKey(response.data.rawKey);
    },
  });

  const copyKey = async (key: string) => {
    await navigator.clipboard.writeText(key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isDisabled) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-purple-100 rounded-xl">
            <Key className="h-6 w-6 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('api_keys.title')}</h1>
            <p className="text-sm text-gray-500">{t('api_keys.subtitle')}</p>
          </div>
        </div>

        <div className="glass-card p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
            <Shield className="h-8 w-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('api_keys.feature_off_title')}</h2>
          <p className="text-gray-600 max-w-lg mx-auto mb-4">
            {t('api_keys.feature_off_body')}
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-md mx-auto">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-700 text-left">
                {t('api_keys.feature_off_warning')}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-purple-100 rounded-xl">
            <Key className="h-6 w-6 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('api_keys.title')}</h1>
            <p className="text-sm text-gray-500">{t('api_keys.subtitle_active')}</p>
          </div>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          {t('api_keys.create_key')}
        </button>
      </div>

      {/* Info banner */}
      <div className="glass-card p-4 border-l-4 border-blue-400">
        <div className="flex items-start gap-3">
          <Shield className="h-5 w-5 text-blue-500 mt-0.5 shrink-0" />
          <div className="text-sm text-gray-600">
            <p className="font-medium text-gray-900 mb-1">{t('api_keys.usage_title')}</p>
            <p>Envoyez l'en-tête <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">X-API-Key: sk_...</code> avec chaque requête.</p>
            <p className="mt-1">Documentation complète disponible sur <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">/docs</code></p>
          </div>
        </div>
      </div>

      {/* New key banner */}
      {newKey && (
        <div className="glass-card p-4 border-l-4 border-amber-400 bg-amber-50">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 mb-1">{t('api_keys.copy_now_title')}</p>
              <p className="text-sm text-gray-600 mb-2">{t('api_keys.copy_now_body')}</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-white border border-gray-200 px-3 py-2 rounded-lg text-sm font-mono text-gray-900 overflow-x-auto">
                  {newKey}
                </code>
                <button
                  onClick={() => copyKey(newKey)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors shrink-0"
                >
                  {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                  {copied ? t('common.copied') : t('common.copy')}
                </button>
              </div>
            </div>
            <button onClick={() => setNewKey(null)} className="p-1 hover:bg-amber-100 rounded">
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>
        </div>
      )}

      {/* Keys table */}
      <div className="glass-card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
          </div>
        ) : keys.length === 0 ? (
          <div className="text-center py-12">
            <Key className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">{t('api_keys.no_key')}</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 text-purple-600 hover:text-purple-700 text-sm font-medium"
            >
              {t('api_keys.create_first')}
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('api_keys.th_name')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('api_keys.th_prefix')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('api_keys.th_permissions')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('api_keys.th_last_used')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('api_keys.th_expiration')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('api_keys.th_status')}</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('api_keys.th_actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {keys.map((key) => (
                  <tr key={key.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-gray-900">{key.name}</span>
                    </td>
                    <td className="px-4 py-3">
                      <code className="text-sm font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                        {key.keyPrefix}...
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {key.permissions.slice(0, 3).map((p) => (
                          <span key={p} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            {p}
                          </span>
                        ))}
                        {key.permissions.length > 3 && (
                          <span className="text-xs text-gray-400">+{key.permissions.length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-500">
                        {key.lastUsedAt
                          ? new Date(key.lastUsedAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
                          : t('api_keys.never')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {key.expiresAt ? (
                        <span className={clsx(
                          'text-sm',
                          new Date(key.expiresAt) < new Date() ? 'text-red-600' : 'text-gray-500',
                        )}>
                          {new Date(key.expiresAt).toLocaleDateString('fr-FR')}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">{t('api_keys.never')}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleMutation.mutate({ id: key.id, isActive: !key.isActive })}
                        className="flex items-center gap-1.5"
                      >
                        {key.isActive ? (
                          <><ToggleRight className="h-5 w-5 text-green-500" /><span className="text-xs text-green-700 font-medium">{t('api_keys.active')}</span></>
                        ) : (
                          <><ToggleLeft className="h-5 w-5 text-gray-400" /><span className="text-xs text-gray-500">{t('api_keys.inactive')}</span></>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => rotateMutation.mutate(key.id)}
                          disabled={rotateMutation.isPending}
                          className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                          title={t('api_keys.rotate')}
                        >
                          <RefreshCw className={clsx('h-4 w-4', rotateMutation.isPending && 'animate-spin')} />
                        </button>
                        {confirmDelete === key.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => deleteMutation.mutate(key.id)}
                              className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                            >
                              {t('api_keys.confirm')}
                            </button>
                            <button
                              onClick={() => setConfirmDelete(null)}
                              className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                            >
                              {t('api_keys.cancel')}
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmDelete(key.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title={t('api_keys.delete')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create modal */}
      {showCreateModal && (
        <CreateApiKeyModal
          onClose={() => setShowCreateModal(false)}
          onCreated={(rawKey) => {
            setNewKey(rawKey);
            setShowCreateModal(false);
            queryClient.invalidateQueries({ queryKey: ['api-keys'] });
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// Create Modal
// ============================================================================

function CreateApiKeyModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (rawKey: string) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [permissions, setPermissions] = useState<string[]>(['devices:read', 'positions:read', 'geofences:read', 'alerts:read']);
  const [hasExpiry, setHasExpiry] = useState(false);
  const [expiresAt, setExpiresAt] = useState('');

  const createMutation = useMutation({
    mutationFn: () =>
      apiKeysApi.create({
        name,
        permissions,
        expiresAt: hasExpiry && expiresAt ? new Date(expiresAt).toISOString() : undefined,
      }),
    onSuccess: (response) => {
      onCreated(response.data.rawKey);
    },
  });

  const togglePermission = (perm: string) => {
    setPermissions((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">{t('api_keys.modal_create_title')}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('api_keys.name_placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500/20"
            />
          </div>

          {/* Permissions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
            <div className="grid grid-cols-1 gap-2">
              {AVAILABLE_PERMISSIONS.map((perm) => (
                <label
                  key={perm.value}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={permissions.includes(perm.value)}
                    onChange={() => togglePermission(perm.value)}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">{perm.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Expiration */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer mb-2">
              <input
                type="checkbox"
                checked={hasExpiry}
                onChange={(e) => setHasExpiry(e.target.checked)}
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span className="text-sm font-medium text-gray-700">Date d'expiration</span>
              <Clock className="h-4 w-4 text-gray-400" />
            </label>
            {hasExpiry && (
              <input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500/20"
              />
            )}
          </div>
        </div>

        {createMutation.isError && (
          <p className="mt-3 text-sm text-red-600">Erreur lors de la création de la clé</p>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={() => createMutation.mutate()}
            disabled={!name.trim() || permissions.length === 0 || createMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
          >
            {createMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Key className="h-4 w-4" />
            )}
            Créer
          </button>
        </div>
      </div>
    </div>
  );
}
